import { configureStore } from '@reduxjs/toolkit';
import { RootAction, RootState, Services } from 'MyTypes';
import { createEpicMiddleware } from 'redux-observable';

import services from '../services';
import rootEpic from './root-epic';
import createRootReducer from './root-reducer';

const epicMiddleware = createEpicMiddleware<RootAction, RootAction, RootState, Services>({
    dependencies: services,
});

const OMITTED = '<<omitted from devtools>>';

// unsanitized, devtools' per-action state snapshots (~34MB) stall recording
const omitBulkySlices = (state) => ({
    ...state,
    entityHierarchies: OMITTED,
    resourceLists: OMITTED,
    dataExplorer: {
        ...state.dataExplorer,
        dataResponsesByType: OMITTED,
        chartDataResponsesByType: OMITTED,
    },
});

const BULKY_ACTION_TYPES = ['QUERY_DATA_SUCCESS', 'QUERY_CHART_DATA_SUCCESS'];

const omitBulkyPayload = (action) =>
    BULKY_ACTION_TYPES.includes(action.type) ? { ...action, payload: OMITTED } : action;

// not a blanket ^GET_, which would also hide the granularity flows
const BOOTSTRAP_ACTIONS_DENYLIST = [
    '^GET_(AGE_GROUPS|ANNOUNCEMENTS|CAUSES|CAUSE_ICD_CODES|COUNTRY_LOCATIONS_SDI)_',
    '^GET_(CROS|DATA_COLLECTIONS|ETIOLOGIES|IMPAIRMENTS|INJURY)_',
    '^GET_(LOCATIONS|ORGANIZATION|RACES|RISKS|ROUNDS|SEQUELAS|SURVEYS|VEHSS)_',
    '^GET_(FAVORITE_RESOURCES|PERMALINK_RESOURCES|GROUP_CONTENT_RESOURCES)_',
    '^GET_(RESOURCE_LISTS|PUBLIC_RESOURCE_LISTS|SIDEBAR_LINKS)_',
];

export function createStore(initialState?: Partial<RootState>) {
    const store = configureStore({
        reducer: createRootReducer(),
        preloadedState: initialState as RootState,
        middleware: (getDefaultMiddleware) =>
            getDefaultMiddleware({
                thunk: true,
                serializableCheck: false,
                immutableCheck: false,
            }).concat(epicMiddleware),
        devTools:
            config.env === 'production'
                ? false
                : {
                      maxAge: 200,
                      actionsDenylist: BOOTSTRAP_ACTIONS_DENYLIST,
                      actionSanitizer: omitBulkyPayload,
                      stateSanitizer: omitBulkySlices,
                  },
    });

    epicMiddleware.run(rootEpic);

    return store;
}
